import { Dispatch, FC, SetStateAction, useState } from 'react';
import MerchantBrandingBlock from '../../components/organisms/MerchantBrandingBlock/MerchantBrandingBlock';
import { Transaction } from '../../services/types';
import { useBankLogo } from '../../utils/bankLogos';
import Header from '../../components/organisms/Header/Header';
import Field from '../../components/atoms/Field/Field';
import InfoBlock from '../../components/organisms/InfoBlock/InfoBlock';
import Button from '../../components/atoms/Button/Button';
import Container from '../../components/atoms/Container/Container';
import Labeled from '../../components/atoms/Labeled/Labeled';
import QR from '../../components/atoms/QR/QR';
import SupportButton from '../../components/atoms/SupportButton/SupportButton';
import useTheme from '../../hooks/useTheme';
import AmountAlert from '../../components/organisms/AmountAlert/AmountAlert';
import { showsAmountChangeNotice } from '../../services/amountChange';
import type { DeclineContext } from '../../services/types';
import { ButtonGroup } from '../../components/ButtonGroup/ButtonGroup';
import { getCurrencySymbol } from '../../services/utils';
import styles from './QrPage.module.scss';

interface QrPageProps {
  transaction: Transaction;
  timeLeft: string;
  ticketId: string;
  onCancel: () => void;
  /** Cancel as a refusal of the adjusted amount. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  onConfirm: () => void;
  setShowConfirm: Dispatch<SetStateAction<boolean>>;
}

/**
 * Оплата по QR (CPAYMENT-1637).
 *
 * У такого реквизита нет номера — провайдер отдаёт ссылку, она приходит в
 * `requisite.paymentUrl`. Показываем сам код (сканировать с телефона) и кнопку
 * перехода (открыть с десктопа); дополнительные поля — банк и получателя —
 * рисуем, только если провайдер их прислал.
 */
const QrPage: FC<QrPageProps> = ({
  transaction,
  ticketId,
  onCancel,
  onDeclineOffer,
  onConfirm,
  timeLeft,
  setShowConfirm,
}) => {
  const { requisite, amount } = transaction;
  const theme = useTheme();
  const [isProcessing, setIsProcessing] = useState(false);

  const { src: bankLogoSrc, hasLogo: hasBankLogo } = useBankLogo(
    requisite.acquiringBank
  );

  const handleConfirmClick = async () => {
    if (isProcessing) return;
    if (transaction?.simpleConfirmation === false) {
      setShowConfirm(true);
      return;
    }
    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Header
        transaction={transaction}
        ticketId={ticketId}
        timeLeft={timeLeft}
      />

      {transaction.merchant.toLowerCase() === 'vocxod' && (
        <div style={{ marginBottom: '28px' }}>
          <SupportButton userId={transaction.externalUserId} />
        </div>
      )}
      <Container>
        <MerchantBrandingBlock
          merchant={transaction.merchant}
          className={styles.imageWrapper}
        />
        <div className={styles.fieldGroup}>
          <Field text='Оплата по QR-коду' bold />

          {amount && (
            <Labeled width='100%'>
              <span>Сумма перевода</span>
              <Field
                text={amount}
                copy
                currency={getCurrencySymbol(requisite.currency)}
              />
            </Labeled>
          )}

          <Labeled>
            <span>Отсканируйте код камерой телефона или откройте ссылку</span>
            <div className={styles.qrWrapper}>
              <QR
                data={requisite.paymentUrl || ''}
                size={180}
                bgColor={theme === 'dark' ? '#1E2B3E' : '#ffffff'}
                fgColor={theme === 'dark' ? '#ffffff' : '#0e1827'}
              />
            </div>
          </Labeled>

          {requisite.acquiringBank && (
            <Labeled>
              <span>Банк</span>
              {hasBankLogo ? (
                <Field image={bankLogoSrc} />
              ) : (
                <Field text={requisite.acquiringBank.toUpperCase()} />
              )}
            </Labeled>
          )}

          {requisite?.name && (
            <Labeled>
              <span>Получатель</span>
              <Field text={requisite.name} />
            </Labeled>
          )}

          {transaction?.simpleConfirmation === false && (
            <Labeled>
              <span>Инструкция</span>
              <InfoBlock
                data=''
                confirm={true}
                merchant={transaction?.merchant}
              />
            </Labeled>
          )}
          {transaction?.requisite?.userManual && (
            <Labeled>
              <span>Инструкция</span>
              <InfoBlock data={transaction.requisite.userManual} />
            </Labeled>
          )}

          <a
            className={styles.goLink}
            href={requisite.paymentUrl || ''}
            target='_blank'
            rel='noopener noreferrer'
          >
            <Button variant='primary' dataSentryElement='leave-to-pay'>
              Перейти к оплате
            </Button>
          </a>

          <ButtonGroup>
            <Button
              variant='secondary'
              onClick={onCancel}
              disabled={isProcessing}
              dataSentryElement='cancel-ticket'
            >
              Отмена
            </Button>
            <Button
              onClick={handleConfirmClick}
              variant='primary'
              disabled={isProcessing}
              dataSentryElement='paid'
            >
              {isProcessing ? 'Проверяем платёж…' : 'Далее'}
            </Button>
          </ButtonGroup>
        </div>
        {showsAmountChangeNotice(transaction) && (
          <AmountAlert
            transaction={transaction}
            onDecline={() => onDeclineOffer('amount_change')}
          />
        )}
      </Container>
    </>
  );
};

export default QrPage;
